import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectEffectiveDate,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateMultiFamilyUnitsAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];
  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    !subjectImprovements ||
    !compImprovements ||
    !subjectPropertyIdentification ||
    isUndefined(depreciationRatio) ||
    !effectiveDate
  ) {
    return undefined;
  }

  const {
    gla: subjGba,
    craftsmanQuality: quality,
    propertyType: subjPropertyType,
  } = subjectImprovements;
  const {
    gla: compGba,
    craftsmanQuality: compQuality,
    propertyType: compPropertyType,
  } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  const subjUnits =
    subjPropertyType === 'multiFamily' ? subjectImprovements.numberOfUnits : undefined;
  const compUnits = compPropertyType === 'multiFamily' ? compImprovements.numberOfUnits : undefined;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: subjUnits,
      compUnitAmount: compUnits,
    });
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (
    isUndefined(zipCode) ||
    isUndefined(subjGba) ||
    isUndefined(compGba) ||
    isUndefined(subjUnits) ||
    isUndefined(compUnits) ||
    isUndefined(quality) ||
    isUndefined(compQuality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1
  ) {
    return undefined;
  }

  return adjCalculator.getMultiFamilyUnitsAdj({
    subject: {
      quality,
      avgUnitSqft: AloftArea.schema.parse(subjGba),
      units: subjUnits,
      zipCode,
    },
    comp: {
      avgUnitSqft: AloftArea.schema.parse(compGba),
      units: compUnits,
      quality: compQuality,
      zipCode,
    },
    depreciationRatio,
  }).adjustment;
};

export const selectMultiFamilyUnitsAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    (_state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'units'),
  ],
  calculateMultiFamilyUnitsAdj,
);

export const selectMultiFamilyUnitsAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'units'),
  ],
  (
    adjustmentProps,
    improvements,
    propertyIdentification,
    depreciationRatio,
    effectiveDate,
    compSequence,
    compTypes,
    salesComparisonApproach,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateMultiFamilyUnitsAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatio,
        effectiveDate,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedMultiFamilyUnitsAdj = createPriceSelector(
  [selectMultiFamilyUnitsAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'units', adjSettings),
);

export const selectAppliedMultiFamilyUnitsAdjDisplay = createSelector(
  [selectAppliedMultiFamilyUnitsAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);
